// billing/aside.js — Modal de creación de comprobantes fiscales
import Swal from "sweetalert2";
import AuthHelpers from "@/helpers/authHelpers";

// ─── Helpers de moneda AR (. miles, , decimal) ────────────────────────────────
const formatAR = (num, decimals = 2) =>
    num.toLocaleString('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const parseAR = (str) =>
    parseFloat(String(str).replace(/\./g, '').replace(',', '.')) || 0;

/**
 * Formatea en vivo un input de monto AR mientras el usuario escribe.
 * Agrega puntos de miles a la parte entera y preserva la coma decimal.
 * Mantiene la posición del cursor contando dígitos antes del cursor.
 */
function liveFormatAmount(input) {
    const pos = input.selectionStart;
    const raw = input.value;

    // Contar dígitos antes del cursor para restaurarlo después
    const digitsBeforeCursor = (raw.slice(0, pos).match(/\d/g) || []).length;

    // Separar parte entera y decimal (coma como separador decimal)
    const commaIdx = raw.indexOf(',');
    const hasComma = commaIdx >= 0;
    const intRaw  = (hasComma ? raw.slice(0, commaIdx) : raw).replace(/\D/g, '');
    const decRaw  = hasComma ? raw.slice(commaIdx + 1).replace(/\D/g, '').slice(0, 2) : '';

    // Formatear entero con puntos de miles
    const intFmt = intRaw.length > 3
        ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        : intRaw;

    const formatted = hasComma ? `${intFmt},${decRaw}` : intFmt;
    input.value = formatted;

    // Restaurar cursor: avanzar hasta contar digitsBeforeCursor dígitos
    let counted = 0;
    let newPos  = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
        if (counted >= digitsBeforeCursor) { newPos = i; break; }
        if (formatted[i] >= '0' && formatted[i] <= '9') counted++;
    }
    input.setSelectionRange(newPos, newPos);
}

const BILLING_CREATE_URL = '/api/billing/';
const TICKET_SEARCH_URL  = '/ticket/api/search-by-plate/';

const CBTE_TIPO = {
     6: { code: '006', label: 'FACTURA B',     desc: 'Consumidor Final',       receptor: 'optional', doc_tipo: 99 },
     1: { code: '001', label: 'FACTURA A',     desc: 'Responsable Inscripto',  receptor: 'cuit',     doc_tipo: 80 },
};

let selectedTipo = 6;
let linkedTicket  = null;
let searchTimeout = null;
// True mientras un "Emitir factura" está en curso (POST a AFIP). Permite que otros
// flujos (ej. renovación de abono, que emite ANTES de confirmar el cobro) sepan si
// el modal se cerró por un submit real o porque el operario lo canceló sin emitir.
let submitInProgress = false;

// ─── Init ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    const btn = document.getElementById('asideBtnCrearFactura');
    if (!btn) return;

    btn.addEventListener('click', (e) => {
        e.preventDefault();
        resetModal();
        new bootstrap.Modal(document.getElementById('modalCrearFactura')).show();
    });

    // Apertura con cliente pre-cargado (desde modal buscar cliente)
    document.addEventListener('billing:openConCliente', (e) => {
        const { cuit, customer_name, items, payment_method } = e.detail || {};
        resetModal();
        if (cuit) {
            // Factura A — CUIT obligatorio
            const radioA = document.querySelector('[name="cbte_tipo"][value="1"]');
            if (radioA) { radioA.checked = true; selectedTipo = 1; }
            updateTipoUI(1);

            const inputCuit = document.getElementById('inputCuit');
            if (inputCuit) {
                const digits = String(cuit).replace(/\D/g, '');
                inputCuit.value = digits.length === 11
                    ? `${digits.slice(0,2)}-${digits.slice(2,10)}-${digits.slice(10)}`
                    : cuit;
            }
            const inputNombre = document.getElementById('inputCustomerNameCuit');
            if (inputNombre) inputNombre.value = customer_name || '';
        } else if (customer_name) {
            // Factura B — pre-llenar nombre en el campo opcional
            const inputNombreOpt = document.getElementById('inputCustomerNameOpt');
            if (inputNombreOpt) inputNombreOpt.value = customer_name;
        }

        // Pre-llenar ítems si vienen informados
        if (items && items.length > 0) {
            const list = document.getElementById('itemsList');
            list.innerHTML = '';
            items.forEach(item => addItemRow(item.description || '', item.amount || ''));
            recalcTotal();
        }

        // Preseleccionar el método de pago elegido en la renovación (código de
        // ticket → código de billing) para que factura y arqueo coincidan.
        if (payment_method) {
            const pmMap = { CASH: 'EFECTIVO', CREDIT_CARD: 'TARJETA', DEBIT_CARD: 'DEBITO', MERCADOPAGO: 'MERCADOPAGO' };
            const mapped = pmMap[payment_method] || payment_method;
            const pmSelect = document.getElementById('facturaPaymentMethod');
            if (pmSelect && [...pmSelect.options].some(o => o.value === mapped)) {
                pmSelect.value = mapped;
            }
        }

        new bootstrap.Modal(document.getElementById('modalCrearFactura')).show();
    });

    // Cancelación del modal de facturación: si se cierra SIN un submit en curso
    // (el operario lo descartó sin emitir), avisamos con billing:cancelled para que
    // quien emite "primero factura, después confirmar" (renovación de abono) no
    // quede a la espera ni cobre sin comprobante.
    const modalFacturaEl = document.getElementById('modalCrearFactura');
    if (modalFacturaEl) {
        modalFacturaEl.addEventListener('hidden.bs.modal', () => {
            if (!submitInProgress) document.dispatchEvent(new CustomEvent('billing:cancelled'));
        });
    }

    setupModal();
});

// ─── Setup ────────────────────────────────────────────────────────────────────

function setupModal() {
    // Tipo de comprobante
    document.querySelectorAll('[name="cbte_tipo"]').forEach(r =>
        r.addEventListener('change', () => {
            selectedTipo = parseInt(r.value);
            updateTipoUI(selectedTipo);
        })
    );

    // Toggle vincular ticket
    document.getElementById('chkVincularTicket').addEventListener('change', function () {
        document.getElementById('sectionTicketSearch').classList.toggle('d-none', !this.checked);
        if (!this.checked) {
            clearTicketCard();
            document.getElementById('ticketSearchInput').value = '';
        }
    });

    // Búsqueda de ticket con debounce
    document.getElementById('ticketSearchInput').addEventListener('input', function () {
        clearTimeout(searchTimeout);
        const val = this.value.trim();
        if (val.length < 2) { clearTicketCard(); return; }
        searchTimeout = setTimeout(() => searchTicket(val), 400);
    });

    // Agregar ítem
    document.getElementById('btnAddItem').addEventListener('click', () => addItemRow());

    // Recalc total al editar ítems
    document.getElementById('itemsList').addEventListener('input', recalcTotal);

    // Formatear total al salir del campo (si fue editado manualmente)
    document.getElementById('facturaTotal').addEventListener('blur', () => {
        const val = parseAR(document.getElementById('facturaTotal').value);
        if (val > 0) document.getElementById('facturaTotal').value = formatAR(val, 2);
    });

    // Submit
    document.getElementById('btnEmitirFactura').addEventListener('click', submitFactura);
}

// ─── Reset ────────────────────────────────────────────────────────────────────

function resetModal() {
    selectedTipo = 6;
    linkedTicket = null;

    // Selección de tipo
    document.querySelector('[name="cbte_tipo"][value="6"]').checked = true;
    updateTipoUI(6);

    // Vincular ticket
    document.getElementById('chkVincularTicket').checked = false;
    document.getElementById('sectionTicketSearch').classList.add('d-none');
    document.getElementById('ticketSearchInput').value = '';
    clearTicketCard();

    // Limpiar inputs receptor
    ['inputCuit','inputCustomerNameCuit','inputDocNro','inputCustomerNameOpt'].forEach(id => {
        const el = document.getElementById(id);
        if (el) { el.value = ''; el.classList.remove('is-invalid'); }
    });
    const chkExento = document.getElementById('chkIvaExento');
    if (chkExento) chkExento.checked = false;
    const docTipo = document.getElementById('selectDocTipo');
    if (docTipo) docTipo.value = '96';

    // Ítems — empezar con "HORAS"
    const list = document.getElementById('itemsList');
    list.innerHTML = '';
    addItemRow('HORAS', '');

    // Total y pago
    document.getElementById('facturaTotal').value = '';
    document.getElementById('facturaPaymentMethod').value = 'EFECTIVO';
}

// ─── Tipo UI ──────────────────────────────────────────────────────────────────

function updateTipoUI(tipo) {
    // Highlight cards
    document.querySelectorAll('.cbte-tipo-card').forEach(card => {
        const isSelected = parseInt(card.dataset.tipo) === tipo;
        card.classList.toggle('border-primary', isSelected);
        card.classList.toggle('bg-primary',     isSelected);
        card.classList.toggle('bg-opacity-10',  isSelected);
        card.classList.toggle('text-primary',   isSelected);
    });

    const { receptor } = CBTE_TIPO[tipo];
    const sectionReceptor  = document.getElementById('sectionReceptor');
    const receptorCuit     = document.getElementById('receptorCuit');
    const receptorOptional = document.getElementById('receptorOptional');

    sectionReceptor.classList.toggle('d-none', receptor === 'none');
    receptorCuit.classList.toggle('d-none',     receptor !== 'cuit');
    receptorOptional.classList.toggle('d-none', receptor !== 'optional');
}

// ─── Items ────────────────────────────────────────────────────────────────────

function addItemRow(desc = '', amount = '') {
    const list = document.getElementById('itemsList');
    const row  = document.createElement('div');
    row.className = 'row g-2 mb-1 item-row align-items-center';
    row.innerHTML = `
        <div class="col-6">
            <input type="text" class="form-control form-control-sm item-desc"
                   placeholder="Descripción del servicio" value="${desc}" maxlength="60">
        </div>
        <div class="col-5">
            <div class="input-group input-group-sm">
                <span class="input-group-text">$</span>
                <input type="text" inputmode="decimal" class="form-control item-amount"
                       placeholder="0,00" value="${amount}">
            </div>
        </div>
        <div class="col-1 d-flex align-items-center">
            <button type="button" class="btn btn-sm btn-outline-danger btn-remove-item px-2">
                <i class="fas fa-times fa-xs"></i>
            </button>
        </div>`;

    row.querySelector('.btn-remove-item').addEventListener('click', () => {
        if (list.querySelectorAll('.item-row').length > 1) {
            row.remove();
            recalcTotal();
        }
    });

    const amountInput = row.querySelector('.item-amount');
    amountInput.addEventListener('input', () => {
        liveFormatAmount(amountInput);
        recalcTotal();
    });
    amountInput.addEventListener('blur', () => {
        const val = parseAR(amountInput.value);
        if (val > 0) amountInput.value = formatAR(val, val !== Math.floor(val) ? 2 : 0);
        recalcTotal();
    });

    list.appendChild(row);
}

function recalcTotal() {
    let sum = 0;
    document.querySelectorAll('.item-amount').forEach(i => { sum += parseAR(i.value); });
    document.getElementById('facturaTotal').value = sum > 0 ? formatAR(sum, 2) : '';
}

// ─── Ticket search ────────────────────────────────────────────────────────────

async function searchTicket(query) {
    try {
        const resp = await fetch(`${TICKET_SEARCH_URL}?license_plate=${encodeURIComponent(query)}`, { credentials: 'include' });
        const data = await resp.json();
        if (data.success && data.found) {
            linkedTicket = data.ticket;
            showTicketCard(data.ticket);
        } else {
            linkedTicket = null;
            showTicketNotFound(query);
        }
    } catch { linkedTicket = null; clearTicketCard(); }
}

function showTicketCard(t) {
    const el  = document.getElementById('ticketResultCard');
    const dt  = new Date(t.entry_time);
    const fmt = dt.toLocaleDateString('es-AR') + ' ' + dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    const amountFmt = t.total_amount !== null && t.total_amount !== undefined
        ? `<strong>$${parseFloat(t.total_amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>`
        : '<span class="text-warning">importe no disponible</span>';

    el.innerHTML = `
        <div class="alert alert-success py-2 mb-0 d-flex align-items-center gap-2">
            <i class="fas fa-check-circle text-success flex-shrink-0"></i>
            <div class="flex-grow-1">
                <strong>${t.ticket_number}</strong> — ${t.license_plate}
                <small class="d-block text-muted">
                    Entrada: ${fmt} · ${t.vehicle_type} ·
                    <span class="badge bg-${t.status==='OPEN'?'success':'secondary'} fs-10">${t.status==='OPEN'?'Activo':'Cerrado'}</span>
                    · <strong>${amountFmt}</strong>
                </small>
            </div>
            <button type="button" class="btn-close btn-close-sm" id="btnClearTicket"></button>
        </div>`;
    el.classList.remove('d-none');

    // Auto-llenar ítem HORAS siempre que haya ticket asociado
    // Si total_amount es null → dejar importe vacío para completar manualmente
    const list = document.getElementById('itemsList');
    list.innerHTML = '';
    const amt = t.total_amount !== null && t.total_amount !== undefined
        ? formatAR(parseFloat(t.total_amount), 2)
        : '';
    addItemRow('HORAS', amt);
    recalcTotal();
    if (t.payment_method) {
        const pmMap = { CASH: 'EFECTIVO', CREDIT_CARD: 'TARJETA', DEBIT_CARD: 'DEBITO', MERCADOPAGO: 'MERCADOPAGO' };
        const mapped = pmMap[t.payment_method] || t.payment_method;
        const pmSelect = document.getElementById('facturaPaymentMethod');
        if ([...pmSelect.options].some(o => o.value === mapped)) {
            pmSelect.value = mapped;
        }
    }

    document.getElementById('btnClearTicket').addEventListener('click', () => {
        clearTicketCard();
        document.getElementById('ticketSearchInput').value = '';
        document.getElementById('chkVincularTicket').checked = false;
        document.getElementById('sectionTicketSearch').classList.add('d-none');
    });
}

function showTicketNotFound(q) {
    const el = document.getElementById('ticketResultCard');
    el.innerHTML = `<div class="alert alert-warning py-2 mb-0"><i class="fas fa-exclamation-circle me-2"></i>No se encontró ticket para "<strong>${q}</strong>"</div>`;
    el.classList.remove('d-none');
}

function clearTicketCard() {
    const el = document.getElementById('ticketResultCard');
    el.innerHTML = '';
    el.classList.add('d-none');
    linkedTicket = null;
}

// ─── Submit ───────────────────────────────────────────────────────────────────

async function submitFactura() {
    const { receptor, doc_tipo: defaultDocTipo } = CBTE_TIPO[selectedTipo];

    // Validar receptor
    let doc_tipo      = defaultDocTipo;
    let doc_nro       = '0';
    let customer_name = '';

    if (receptor === 'cuit') {
        const inputCuit = document.getElementById('inputCuit');
        const cuit = inputCuit.value.trim().replace(/[-\s]/g, '');
        if (!cuit || !/^\d{10,11}$/.test(cuit)) {
            inputCuit.classList.add('is-invalid');
            inputCuit.focus();
            return;
        }
        inputCuit.classList.remove('is-invalid');
        doc_tipo      = 80;
        doc_nro       = cuit;
        customer_name = document.getElementById('inputCustomerNameCuit').value.trim();
    } else if (receptor === 'optional') {
        const docNroEl = document.getElementById('inputDocNro');
        doc_tipo      = parseInt(document.getElementById('selectDocTipo').value) || 99;
        doc_nro       = docNroEl.value.trim() || '0';
        customer_name = document.getElementById('inputCustomerNameOpt').value.trim();
    }

    // Validar ítems
    const items = [];
    let invalid = false;
    document.querySelectorAll('.item-row').forEach(row => {
        const desc   = row.querySelector('.item-desc').value.trim();
        const amount = parseAR(row.querySelector('.item-amount').value);
        if (!desc || amount <= 0) { invalid = true; return; }
        items.push({ description: desc, amount: amount.toFixed(2) });
    });
    if (invalid || items.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Ítems incompletos', text: 'Completá descripción e importe de cada ítem.' });
        return;
    }

    // Validar total
    const total = parseAR(document.getElementById('facturaTotal').value);
    if (total <= 0) {
        Swal.fire({ icon: 'warning', title: 'Total inválido', text: 'El total debe ser mayor a cero.' });
        return;
    }

    const ivaExento = (selectedTipo === 1 || selectedTipo === 6) && !!(document.getElementById('chkIvaExento')?.checked);

    const payload = {
        cbte_tipo:      selectedTipo,
        doc_tipo,
        doc_nro,
        customer_name,
        items,
        total:          total.toFixed(2),
        imp_neto:       total.toFixed(2),
        imp_iva:        '0.00',
        iva_exento:     ivaExento,
        payment_method: document.getElementById('facturaPaymentMethod').value,
        ticket_id:      linkedTicket ? linkedTicket.id : null,
        station_id:     'CAJA_01',
        skip_print:     document.getElementById('chkSkipPrint').checked,
    };

    // Cerrar modal y mostrar loading. Marcamos submit en curso para que el cierre
    // del modal NO se interprete como cancelación (ver billing:cancelled).
    submitInProgress = true;
    bootstrap.Modal.getInstance(document.getElementById('modalCrearFactura')).hide();

    Swal.fire({
        title: 'Solicitando CAE a AFIP...',
        text: 'Esto puede demorar unos segundos.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
    });

    try {
        const resp = await AuthHelpers.fetchWithCSRF(BILLING_CREATE_URL, { method: 'POST', body: JSON.stringify(payload) });
        const data = await resp.json();
        Swal.close();

        if (resp.ok || resp.status === 207) {
            const tipoInfo = CBTE_TIPO[selectedTipo];
            // El comprobante se creó (con CAE) o quedó pendiente (207, AFIP caído):
            // en ambos casos es un submit exitoso, no una cancelación.
            submitInProgress = false;
            document.dispatchEvent(new CustomEvent('billing:created', { detail: data }));
            if (data.cae) {
                Swal.fire({
                    icon:  'success',
                    title: 'Comprobante Emitido',
                    html:  `
                        <div class="text-start">
                            <p class="mb-1"><strong>Cód. ${tipoInfo.code} — ${tipoInfo.label}</strong></p>
                            <p class="mb-1">N° <strong>${String(data.number).padStart(8,'0')}</strong> · PV <strong>${String(data.pv).padStart(5,'0')}</strong></p>
                            <p class="mb-1 font-monospace text-muted small">CAE: ${data.cae}</p>
                            <p class="mb-0 ${data.printed ? 'text-success' : 'text-warning'}">
                                <i class="fas fa-${data.printed ? 'check' : 'exclamation-triangle'} me-1"></i>
                                ${data.printed ? 'Enviado a impresora' : 'No se pudo enviar a impresora'}
                            </p>
                        </div>`,
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon:  'warning',
                    title: 'Guardado sin CAE',
                    html:  `<p>El comprobante se guardó pero AFIP devolvió un error:</p><p class="text-danger small">${data.afip_error_detail || data.afip_error || 'Error desconocido'}</p>`,
                });
            }
        } else {
            // El comprobante NO se creó (validación rechazada): para el flujo que
            // emite antes de confirmar, es como cancelar (no se cobra el abono).
            submitInProgress = false;
            document.dispatchEvent(new CustomEvent('billing:cancelled'));
            const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('<br>');
            Swal.fire({ icon: 'error', title: 'Error de validación', html: msgs || 'Error al emitir comprobante.' });
        }
    } catch (err) {
        // Falló la conexión con nuestro servidor: el comprobante no se creó.
        submitInProgress = false;
        document.dispatchEvent(new CustomEvent('billing:cancelled'));
        Swal.close();
        Swal.fire({ icon: 'error', title: 'Error de conexión', text: err.message });
    }
}
